import React, {
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import styles from "./SuperAdminUsers.module.scss";
import Button from "@src/components/button/Button";
import { BodyColumn, HeaderColumn } from "@src/components/table/Table";
import { SelectOption, User, UserStatus, UserRoleEnum } from "@src/types";
import { useClientPagination } from "@src/hooks/useClientPagination";
import {
  useQueryDictionary,
  useQuerySllProgramWithCountry,
} from "@src/hooks/useQueryDictionary";
import InviteModal from "./invite-modal/InviteModal";
import { useUser } from "@src/hooks/useUser";
import { useQueryParams } from "@src/hooks/useQueryParams";
import { useHistory } from "react-router-dom";
import { LordIconRow } from "@src/components/lord-icon";
import { IconCopy, IconLink, IconEdit, IconPassword } from "@src/constants";
import SentryTrackingButton from "@src/components/sentry-tracking/SentryTrackingButton";
import Tooltip from "@src/components/tooltip/Tooltip";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useToast } from "@src/hooks/useToast";
import Checkbox from "@src/components/checkbox/Checkbox";
import { countryOptions, countries } from "@constants";
import {
  useUsersLazyQuery,
  useGetAccountSchoolsLazyQuery,
  useCopyPasswordLinkMutation,
  useEmailPasswordLinkMutation,
  useAccountSchoolsLazyQuery,
  useCustomersLazyQuery,
} from "@api/graphql-codegen";
import { isEmptyObjectWithoutUndefined } from "@src/utils/validation";
import NormalModal from "@src/components/modal/normal-modal/NormalModal";
import IconWarning from "@src/assets/icons/modal-warning.svg?react";
import NotificationIcon from "@src/assets/icons/notification.svg?react";
import AccountAddModal from "../accounts/add-modal/AddModal";
import SchoolAddModal from "../schools/add-modal/AddModal";
import {
  AdminGrid,
  AdminTable,
  CountryFlag,
  RoleIcon,
  ActionButtons,
  TableLink,
} from "@src/components/admin-grid";
import { InviteSlidingPanel } from "./invite-slide/inviteSlidePanel";
import FilterSection from "@src/components/filter-section";
import Spinner from "@src/components/spinner/Spinner";
import { logger } from "../../../utils/logger";

// Add global style to force icons to be visible on hover and ensure ellipsis works
const GlobalStyle = () => {
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      tr:hover .${styles.iconGroup} {
        display: flex !important;
        opacity: 1 !important;
        visibility: visible !important;
      }
      
      /* Force ellipsis to show up */
      div[class*="SuperAdminUsers_cellContent"] {
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
        max-width: calc(100% - 20px) !important;
      }
      
      /* Set consistent widths for columns */
      .emailColumn, .accountColumn, .schoolColumn {
        width: 250px !important;
        min-width: 250px !important;
        max-width: 250px !important;
      }
      
      /* Name column: use full width minus icon so names are not cut off */
      .nameColumn div[class*="SuperAdminUsers_cellContent"] {
        max-width: calc(100% - 40px) !important;
      }
      
      .emailColumn div[class*="SuperAdminUsers_cellContent"],
      .accountColumn div[class*="SuperAdminUsers_cellContent"],
      .schoolColumn div[class*="SuperAdminUsers_cellContent"] {
        max-width: calc(100% - 40px) !important;
      }
    `;

    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  return null;
};

type Props = {};

export type UsersResponse = {
  users: User[];
};

const rolesOptions = [
  { value: UserRoleEnum.User, label: UserRoleEnum.User },
  { value: UserRoleEnum.Admin, label: UserRoleEnum.Admin },
  { value: UserRoleEnum.DistrictAdmin, label: UserRoleEnum.DistrictAdmin },
  { value: UserRoleEnum.SuperAdmin, label: UserRoleEnum.SuperAdmin },
];

function SuperAdminUsers(props: Props) {
  const [me] = useUser();
  const queryParams = useQueryParams();
  const showInviteParam = queryParams.get("invite");
  const showBulkInviteParam = queryParams.get("bulk-invite");
  const history = useHistory();
  const [isReset, setIsReset] = useState(false);
  const [searchUsers, { data, loading }] = useUsersLazyQuery({
    fetchPolicy: "network-only",
    onCompleted(data) {
      if (data && data.users && data.users.length > 0) {
        setIsReset(false);
      }
    },
  });
  const customersAllOptions = useQueryDictionary({ dictionary: "Customers" });
  const schoolAllOptions = useQueryDictionary({ dictionary: "Schools" });
  const [accountFilter, setAccountFilter] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [bulkInviteSlide, setBulkInviteSlide] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | undefined>();
  const [perPage] = useState(50);
  const [search, setSearch] = useState("");
  const { successToast, errorToast } = useToast();

  const [
    getSchools,
    { data: accountSchoolsData, loading: isAccountSchoolsFetching },
  ] = useGetAccountSchoolsLazyQuery({ fetchPolicy: "cache-first" });
  const [getAccountSchools, { data: fullSchoolData }] =
    useAccountSchoolsLazyQuery({
      fetchPolicy: "network-only",
      onError: (error) => {
        logger.error("Error fetching account schools:", error);
      },
    });
  const [getCustomers, { data: fullAccountData }] = useCustomersLazyQuery({
    fetchPolicy: "network-only",
    onError: (error) => {
      logger.error("Error fetching customers:", error);
    },
  });

  const [emptySchool, setEmptySchool] = useState(false);
  const [emptyAccount, setEmptyAccount] = useState(false);
  const [hasNoAdmin, setHasNoAdmin] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [isTrial, setIsTrial] = useState(false);

  const customersOptions = useMemo(
    () =>
      countryFilter
        ? customersAllOptions.filter((option) =>
            option.value.startsWith(countryFilter)
          )
        : customersAllOptions,
    [customersAllOptions, countryFilter]
  );

  const schoolOptions = useMemo(() => {
    if (!countryFilter && !accountFilter) {
      return schoolAllOptions;
    }
    if (!accountFilter && countryFilter) {
      return schoolAllOptions.filter((option) =>
        option.value.startsWith(countryFilter)
      );
    }
    if (
      accountFilter &&
      !isAccountSchoolsFetching &&
      accountSchoolsData?.accountSchools?.schools?.length
    ) {
      return (accountSchoolsData?.accountSchools?.schools || []).map(
        (option) => ({ label: option?.name, value: option?.id } as SelectOption)
      );
    }
    return [];
  }, [
    countryFilter,
    accountFilter,
    isAccountSchoolsFetching,
    accountSchoolsData?.accountSchools?.schools,
    schoolAllOptions,
  ]);

  useEffect(() => {
    if (bulkInviteSlide) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    }
  }, [bulkInviteSlide]);

  const {
    dataSlice: filteredData,
    onChangePage,
    totalFilteredItems,
    currentPage,
  } = useClientPagination({
    // If isReset is true, use an empty array instead of the actual data
    data: isReset ? [] : data?.users || [],
    perPage,
  });

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showSchoolModal, setShowSchoolModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<any>(null);
  const [selectedSchool, setSelectedSchool] = useState<any>(null);
  // Use the useQuerySllProgramWithCountry hook to get program options
  const programOptions = useQuerySllProgramWithCountry();

  const sllProgramList = useMemo(() => {
    // Filter program options based on the selected account's country
    return programOptions
      .filter((p) =>
        p.country?.includes(countries[selectedAccount?.country ?? ""])
      )
      .map((p) => ({
        label: p.label,
        value: p.value,
      }));
  }, [programOptions, selectedAccount?.country]);

  const handleChangeSearch = useCallback(
    (v) => {
      setSearch(v);
    },
    [setSearch]
  );

  const handleBulkInviteSlide = () => setBulkInviteSlide(true);

  const handleModalCloseAction = () => {
    setSelectedUser(undefined);
    setShowInviteModal(false);
    setShowResetPasswordModal(false);
  };

  const handleAccountModalClose = () => {
    setSelectedAccount(null);
    setShowAccountModal(false);
  };

  const handleSchoolModalClose = () => {
    setSelectedSchool(null);
    setSelectedAccount(null);
    setShowSchoolModal(false);
  };

  useEffect(() => {
    if (showInviteParam) {
      history.replace(history.location.pathname);
    }
  }, [history, showInviteParam]);

  // Update the selectedAccount state when the full account data is available
  useEffect(() => {
    if (fullAccountData?.allCustomers && selectedAccount) {
      const fullAccount = fullAccountData.allCustomers.find(
        (c) => c?.id === selectedAccount.id
      );
      if (fullAccount) {
        setSelectedAccount(fullAccount);
      }
    }
  }, [fullAccountData, selectedAccount]);

  useEffect(() => {
    if (showBulkInviteParam) {
      history.replace(history.location.pathname);
      handleBulkInviteSlide();
    }
  }, [history, showBulkInviteParam]);

  const handleChangePage = (page: number) => {
    onChangePage(page);
  };

  const handleEditUser = useCallback(
    (user: User, action: "EDIT" | "PASSWORD_RESET") => () => {
      setSelectedUser(user);
      if (action === "EDIT") {
        setShowInviteModal(true);
      }
      if (action === "PASSWORD_RESET") {
        setShowResetPasswordModal(true);
      }
    },
    []
  );

  const searchObj = useMemo(
    () => ({
      country: countryFilter || undefined,
      keyword: search?.trim() || undefined,
      accountId: accountFilter || undefined,
      schoolId: schoolFilter || undefined,
      role: roleFilter || undefined,
      noAccount: emptyAccount || undefined,
      noSchool: emptySchool || undefined,
      noAdmin: hasNoAdmin || undefined,
      pending: isPending || undefined,
      trial: isTrial || undefined,
    }),
    [
      accountFilter,
      countryFilter,
      emptyAccount,
      emptySchool,
      hasNoAdmin,
      isPending,
      isTrial,
      roleFilter,
      schoolFilter,
      search,
    ]
  );

  const isSearchEmpty = useMemo(
    () => isEmptyObjectWithoutUndefined(searchObj),
    [searchObj]
  );
  const handleSearch = useCallback(() => {
    if (isSearchEmpty) {
      return;
    }
    // Clear the reset flag when searching
    setIsReset(false);
    searchUsers({
      variables: {
        input: searchObj,
      },
    });
  }, [isSearchEmpty, searchObj, searchUsers]);

  const handleResetFilters = useCallback((skipCountry?: boolean) => {
    // Reset all filter states
    setAccountFilter("");
    setSchoolFilter("");
    setRoleFilter("");
    if (!skipCountry) {
      setCountryFilter("");
    }
    setSearch("");
    setIsTrial(false);
    setHasNoAdmin(false);
    setIsPending(false);
    setEmptySchool(false);
    setEmptyAccount(false);

    // Set the reset flag to true to show empty results
    setIsReset(true);
  }, []);

  const handleChangeFilter = useCallback(
    (setter: Dispatch<string>, fieldName?: string) => (o: SelectOption) => {
      setter(o.value);
      if (fieldName === "Account" && o.value) {
        getSchools({ variables: { input: { id: o.value } } });
      } else if (fieldName === "Country") {
        handleResetFilters(true);
      }
    },
    [getSchools, handleResetFilters]
  );

  const handleCopyToClipboard = useCallback(
    (text: string) => {
      successToast(text);
    },
    [successToast]
  );

  const [copyPasswordLink, { loading: copyActionLoading }] =
    useCopyPasswordLinkMutation({
      onCompleted: (data) => {
        navigator.clipboard
          .writeText(data.copyPasswordLink)
          .then(() => {
            handleModalCloseAction();
            successToast("Reset password link copied!");
          })
          .catch((e) => {
            logger.error(
              "Copy to clipboard is not working when DevTool is focused",
              e
            );
          });
      },
      onError: (error) => {
        logger.debug(`[Error]`, error);
      },
    });

  const [emailPasswordLink, { loading: emailActionLoading }] =
    useEmailPasswordLinkMutation({
      onCompleted: (data) => {
        if (data.emailPasswordLink) {
          handleModalCloseAction();
          successToast("Email Sent");
        }
      },
      onError: (error) => {
        logger.debug(`[Error]`, error);
      },
    });

  const handleModalAction = useCallback(
    (userEmail: string, option: "COPY_ACTION" | "EMAIL_ACTION") => async () => {
      try {
        if (option === "COPY_ACTION") {
          await copyPasswordLink({
            variables: { input: { email: userEmail } },
          });
        }
        if (option === "EMAIL_ACTION") {
          await emailPasswordLink({
            variables: { input: { email: userEmail } },
          });
        }
      } catch (e) {
        errorToast((e as any)?.message || "");
      }
    },
    [copyPasswordLink, errorToast, emailPasswordLink]
  );

  const headerColumns: HeaderColumn[] = useMemo(() => {
    return [
      {
        title: "Name",
        sort: {
          key: "name",
          asc: (a: User, b: User) =>
            `${a.firstName} ${a.lastName}`.localeCompare(
              `${b.firstName} ${b.lastName}`
            ),
          desc: (a: User, b: User) =>
            `${b.firstName} ${b.lastName}`.localeCompare(
              `${a.firstName} ${a.lastName}`
            ),
        },
      },
      {
        title: "Email",
        sort: {
          key: "email",
          asc: (a: User, b: User) => a.email.localeCompare(b.email),
          desc: (a: User, b: User) => b.email.localeCompare(a.email),
        },
      },
      {
        title: "Account",
        sort: {
          key: "account",
          asc: (a: User, b: User) =>
            a.customer?.name.localeCompare(b?.customer?.name || "") || 0,
          desc: (a: User, b: User) =>
            b.customer?.name.localeCompare(a?.customer?.name || "") || 0,
        },
      },
      { title: "School" },
      { title: "Country", center: true },
      { title: "Role" },
      { title: "Status" },
      { title: "Trial" },
      { title: "Actions", center: true },
    ];
  }, []);

  const bodyColumns: BodyColumn[] = useMemo(() => {
    return [
      {
        key: (row: User, column, options) => {
          if (!(row?.firstName || row?.lastName)) {
            return <div style={{ color: "red" }}>No Name</div>;
          }
          return (
            <div className={styles.cellWithCopyIcon}>
              <div
                className={styles.cellContentNameColumn}
                title={`${row.firstName} ${row.lastName}`}
              >
                <div
                  style={{
                    width: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                    // maxWidth: 'calc(100% - 40px)'
                  }}
                >
                  {row.firstName} {row.lastName}
                </div>
              </div>
              <div
                className={styles.iconGroup}
                style={{ display: "flex", position: "absolute", right: "-5px" }}
              >
                <ActionButtons
                  tooltipPosition="top"
                  showDelete={false}
                  showView={false}
                  showEdit={false}
                  onCopy={handleCopyToClipboard}
                  copyText={row.id}
                  copyTooltip={`Copy ${row?.firstName || ""} ${
                    row.lastName || ""
                  }'s ID`}
                  isRowHovered={options?.isRowHovered}
                  rowColor="#FFB073"
                />
              </div>
            </div>
          );
        },
        className: "nameColumn",
      },
      {
        key: (row: User, column, options) => {
          if (!row?.email) {
            return <div style={{ color: "red" }}>No Email</div>;
          }
          return (
            <div className={styles.cellWithCopyIcon}>
              <div className={styles.cellContentEmailColumn} title={row.email}>
                <div
                  style={{
                    width: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                  }}
                >
                  {row.email}
                </div>
              </div>
              <div
                className={styles.iconGroup}
                style={{ display: "flex", position: "absolute", right: "-5px" }}
              >
                <ActionButtons
                  tooltipPosition="top"
                  showDelete={false}
                  showView={false}
                  showEdit={false}
                  onCopy={handleCopyToClipboard}
                  copyText={row.email}
                  copyTooltip={`Copy email: ${row.email}`}
                  isRowHovered={options?.isRowHovered}
                  rowColor="#FFB073"
                />
              </div>
            </div>
          );
        },
        className: "emailColumn",
      },
      {
        key: (row: User, column, options) => {
          if (!row?.customer?.name) {
            return <div style={{ color: "red" }}>No Account</div>;
          }
          return (
            <div className={styles.cellWithCopyIcon}>
              <div
                className={styles.cellContentAccountColumn}
                title={row.customer.name}
              >
                <div
                  style={{
                    width: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                    maxWidth: "calc(100% - 30px)",
                    cursor: "pointer",
                  }}
                >
                  <TableLink
                    onClick={() => {
                      if (row.customer) {
                        setSelectedAccount(row.customer);
                        setShowAccountModal(true);
                        getCustomers();
                      }
                    }}
                    title={row.customer.name}
                  >
                    {row.customer.name}
                  </TableLink>
                </div>
              </div>
              <div
                className={styles.iconGroup}
                style={{ display: "flex", position: "absolute", right: "-5px" }}
              >
                <ActionButtons
                  showDelete={false}
                  showView={false}
                  showEdit={false}
                  showCopy={false}
                  isRowHovered={options?.isRowHovered}
                  rowColor="#FFB073"
                  customActions={
                    <>
                      <Tooltip
                        tooltip={`Go to account page: ${row.customer.name}`}
                        position="top"
                      >
                        <div
                          className={styles.iconWrapper}
                          onClick={() => {
                            if (row.customer?.id) {
                              const encodedAccountId = encodeURIComponent(
                                row.customer.id
                              );
                              const encodedAccountName = encodeURIComponent(
                                row.customer.name
                              );
                              const url = `${window.location.origin}/super/accounts/${encodedAccountId}/schools?accountName=${encodedAccountName}`;
                              window.open(url, "_blank");
                            }
                          }}
                        >
                          <LordIconRow
                            src={IconLink}
                            trigger="hover"
                            size={20}
                            isRowHover={options?.isRowHovered}
                            rowColor="#FFB073"
                            colors={{
                              primary: "#274649",
                              secondary: "#FFB073",
                            }}
                          />
                        </div>
                      </Tooltip>
                      <Tooltip
                        tooltip={`Copy account ID: ${row.customer.id}`}
                        position="top"
                      >
                        <CopyToClipboard
                          text={row.customer.id}
                          onCopy={() =>
                            handleCopyToClipboard("Account ID copied!")
                          }
                        >
                          <div
                            className={styles.iconWrapper}
                            style={{ cursor: "pointer" }}
                          >
                            <LordIconRow
                              src={IconCopy}
                              trigger="hover"
                              size={20}
                              isRowHover={options?.isRowHovered}
                              rowColor="#FFB073"
                              colors={{
                                primary: "#274649",
                                secondary: "#FFB073",
                              }}
                            />
                          </div>
                        </CopyToClipboard>
                      </Tooltip>
                    </>
                  }
                />
              </div>
            </div>
          );
        },
        className: "accountColumn",
      },
      {
        key: (row: User, column, options) => {
          if (!row?.schools || row.schools.length === 0) {
            return <div style={{ color: "red" }}>No Schools</div>;
          }
          return (
            <div className={styles.cellWithCopyIcon}>
              <div
                className={styles.cellContentSchoolColumn}
                title={row.schools.map((s) => s?.name).join(", ")}
              >
                <div
                  style={{
                    width: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                    cursor: "pointer",
                  }}
                >
                  {row.schools.map((r, i) => (
                    <React.Fragment key={i}>
                      <TableLink
                        onClick={() => {
                          if (r?.id && row.customer) {
                            setSelectedSchool(r);
                            setSelectedAccount(row.customer);
                            setShowSchoolModal(true);

                            const hasFullData =
                              fullSchoolData?.accountSchools?.schools?.some(
                                (s) => s?.id === r.id
                              );
                            if (!hasFullData) {
                              getAccountSchools({
                                variables: {
                                  input: { id: row.customer.id },
                                },
                              });
                            }
                          }
                        }}
                        title={r?.name}
                      >
                        {r?.name}
                      </TableLink>
                      {(row?.schools || []).length > 0 &&
                        i < (row?.schools || []).length - 1 && <span>, </span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              {row.schools.length === 1 && row.schools[0]?.id && (
                <div
                  className={styles.iconGroup}
                  style={{
                    display: "flex",
                    position: "absolute",
                    right: "-5px",
                  }}
                >
                  <ActionButtons
                    tooltipPosition="top"
                    showDelete={false}
                    showView={false}
                    showEdit={false}
                    onCopy={handleCopyToClipboard}
                    copyText={row.schools[0].id}
                    copyTooltip={`Copy school ID: ${row.schools[0].id}`}
                    isRowHovered={options?.isRowHovered}
                    rowColor="#FFB073"
                  />
                </div>
              )}
            </div>
          );
        },
        className: "schoolColumn",
      },
      {
        key: (row: User, column, options) => {
          if (!row?.id) {
            return <div style={{ color: "red" }}>No Country/ID</div>;
          }
          return <CountryFlag countryCode={row.id.substring(0, 2)} />;
        },
        className: "countryColumn",
      },
      {
        key: (row: User, column, options) => {
          if (!row?.role) {
            return <div style={{ color: "red" }}>No Customer Roles</div>;
          }
          return <RoleIcon role={row.role} />;
        },
        className: "roleColumn",
      },
      {
        key: (row: User, column, options) => {
          if (row?.disabled) {
            return (
              <div className={styles.statusWrapper}>
                <span className={`${styles.badge} ${styles.badgeRed}`}>
                  Disabled
                </span>
              </div>
            );
          }

          if (!row?.status) {
            return <div style={{ color: "red" }}>No Status</div>;
          }

          const pending = row.status === UserStatus.Pending;
          const locked = row.status === UserStatus.Locked;
          return (
            <div className={styles.statusWrapper}>
              <span
                className={`${styles.badge} ${
                  pending ? styles.badgeBlue : locked ? styles.badgeRed : ""
                }`}
              >
                {pending ? "Invite Pending" : row.status}
              </span>
            </div>
          );
        },
        className: "statusColumn",
      },
      {
        key: (row: User, column, options) => {
          return (
            <div
              className={styles.iconWrapperTrial}
              style={{ textAlign: "right" }}
            >
              {row.customer?.trial ? (
                <span
                  style={{ display: "inline-block" }}
                  className={`${styles.badge} ${styles.badgeBlue}`}
                >
                  Trial
                </span>
              ) : null}
            </div>
          );
        },
        className: "trialColumn",
      },
      {
        key: (row: User, column, options) => {
          if (!(row?.status || row?.id || row?.firstName || row?.lastName)) {
            return <div style={{ color: "red" }}>E</div>;
          }

          const pending = row.status === UserStatus.Pending;

          if (pending) {
            return (
              <div style={{ display: "flex", alignItems: "center" }}>
                <ActionButtons
                  showDelete={false}
                  showView={false}
                  showCopy={false}
                  showEdit={false}
                  customActions={
                    <>
                      <div
                        className={styles.iconWrapper}
                        onClick={handleEditUser(row, "EDIT")}
                      >
                        <Tooltip tooltip="Edit user" position="left">
                          <LordIconRow
                            src={IconEdit}
                            trigger="hover"
                            size={20}
                            isRowHover={options?.isRowHovered}
                            rowColor="#FFB073"
                            colors={{
                              primary: "#274649",
                              secondary: "#FFB073",
                            }}
                          />
                        </Tooltip>
                      </div>
                      <div className={styles.iconWrapper}>
                        <Tooltip
                          tooltip={`Copy invite link for ${row.firstName} ${row.lastName}`}
                          position="right"
                        >
                          <CopyToClipboard
                            text={`${
                              window.location.protocol +
                              "//" +
                              window.location.host
                            }/registration?id=${row.id}`}
                            onCopy={() =>
                              handleCopyToClipboard("Invite link copied!")
                            }
                          >
                            <div style={{ cursor: "pointer" }}>
                              <LordIconRow
                                src={IconCopy}
                                trigger="hover"
                                size={20}
                                isRowHover={options?.isRowHovered}
                                rowColor="#FFB073"
                                colors={{
                                  primary: "#274649",
                                  secondary: "#FFB073",
                                }}
                              />
                            </div>
                          </CopyToClipboard>
                        </Tooltip>
                      </div>
                      <SentryTrackingButton
                        entityId={row.id}
                        entityType="user"
                        entityName={`${row.firstName} ${row.lastName}`}
                        buttonText="Track"
                        buttonSize="small"
                        onTrackingAdded={handleSearch}
                        isRowHovered={options?.isRowHovered}
                      />
                    </>
                  }
                />
              </div>
            );
          } else {
            return (
              <div style={{ display: "flex", alignItems: "center" }}>
                <ActionButtons
                  showDelete={false}
                  showView={false}
                  showCopy={false}
                  showEdit={false}
                  customActions={
                    <>
                      <div
                        className={styles.iconWrapper}
                        onClick={handleEditUser(row, "EDIT")}
                      >
                        <Tooltip tooltip="Edit user" position="left">
                          <LordIconRow
                            src={IconEdit}
                            trigger="hover"
                            size={20}
                            isRowHover={options?.isRowHovered}
                            rowColor="#FFB073"
                            colors={{
                              primary: "#274649",
                              secondary: "#FFB073",
                            }}
                          />
                        </Tooltip>
                      </div>
                      <div
                        className={styles.iconWrapper}
                        onClick={handleEditUser(row, "PASSWORD_RESET")}
                      >
                        <Tooltip tooltip="Reset password" position="top">
                          <LordIconRow
                            src={IconPassword}
                            trigger="hover"
                            size={20}
                            isRowHover={options?.isRowHovered}
                            rowColor="#FFB073"
                            colors={{
                              primary: "#274649",
                              secondary: "#FFB073",
                            }}
                          />
                        </Tooltip>
                      </div>
                      <SentryTrackingButton
                        entityId={row.id}
                        entityType="user"
                        entityName={`${row.firstName} ${row.lastName}`}
                        buttonText="Track"
                        buttonSize="small"
                        onTrackingAdded={handleSearch}
                        isRowHovered={options?.isRowHovered}
                      />
                    </>
                  }
                />
              </div>
            );
          }
        },
        className: "actionsColumn",
      },
    ];
  }, [
    handleCopyToClipboard,
    getCustomers,
    fullSchoolData?.accountSchools?.schools,
    getAccountSchools,
    handleEditUser,
  ]);

  // Advanced filters component
  const advancedFiltersContent = (
    <>
      <Checkbox
        id={"hasSchool"}
        checked={emptySchool}
        label={"Has No School"}
        onChange={(e) => setEmptySchool(e)}
      />
      <Checkbox
        id={"hasNoAdmin"}
        checked={hasNoAdmin}
        label={"Has No Admin"}
        onChange={(e) => setHasNoAdmin(e)}
      />
      <Checkbox
        id={"emptyAccount"}
        checked={emptyAccount}
        label={"Has No Account"}
        onChange={(e) => setEmptyAccount(e)}
      />
      <Checkbox
        id={"isPending"}
        checked={isPending}
        label={"Is Pending"}
        onChange={(e) => setIsPending(e)}
      />
      <Checkbox
        id={"isTrial"}
        checked={isTrial}
        label={"Is Trial"}
        onChange={(e) => setIsTrial(e)}
      />
    </>
  );

  // Filter section component using the reusable FilterSection component
  const filterSectionComponent = (
    <FilterSection
      searchValue={search}
      onSearchChange={handleChangeSearch}
      filters={[
        {
          value: countryFilter,
          onChange: handleChangeFilter(setCountryFilter, "Country"),
          label: "Country",
          options: countryOptions,
          showSearch: false,
        },
        {
          value: accountFilter,
          onChange: handleChangeFilter(setAccountFilter, "Account"),
          label: "Account",
          options: customersOptions,
        },
        {
          value: schoolFilter,
          onChange: handleChangeFilter(setSchoolFilter),
          label: "School",
          options: schoolOptions,
          loading: isAccountSchoolsFetching,
          disabled: isAccountSchoolsFetching,
        },
        {
          value: roleFilter,
          onChange: handleChangeFilter(setRoleFilter),
          label: "Role",
          options: rolesOptions,
        },
      ]}
      actionButton={{
        onClick: handleSearch,
        className: styles.searchBtn,
        label: "Search",
        color: "orange",
        size: "normal",
        disabled: isSearchEmpty,
      }}
      advancedFilters={advancedFiltersContent}
      onReset={handleResetFilters}
      resetButtonLabel="Reset"
    />
  );

  // Header actions component
  const headerActionsComponent = (
    <Button
      onClick={handleBulkInviteSlide}
      className={styles.inviteBtn}
      label={"ADD NEW"}
      size={"small"}
    />
  );

  return (
    <>
      <GlobalStyle />
      <AdminGrid
        title="Users"
        filterSection={filterSectionComponent}
        headerActions={headerActionsComponent}
        resultsCount={totalFilteredItems}
        resultsCountLabel="Users"
      >
        {loading ? (
          <div className={styles.spinnerContainer}>
            <Spinner />
          </div>
        ) : (
          <AdminTable
            header={headerColumns}
            body={bodyColumns}
            data={filteredData}
            loading={false}
            currentPage={currentPage}
            perPage={perPage}
            totalItems={totalFilteredItems}
            onPageChange={handleChangePage}
            noItemsText="Please search users with filters."
            color="green"
          />
        )}
      </AdminGrid>

      {showInviteModal && (
        <InviteModal
          me={me!}
          user={selectedUser}
          open={showInviteModal}
          onClose={handleModalCloseAction}
          handleSearch={handleSearch}
        />
      )}

      {showResetPasswordModal && selectedUser && (
        <NormalModal
          logoIcon={<IconWarning />}
          onClose={handleModalCloseAction}
          title={"Password Reset"}
          open={showResetPasswordModal && !!selectedUser}
          description={`Select from the following two options to reset ${selectedUser?.firstName}'s Password.`}
          leftButton={
            <Button
              isLoading={copyActionLoading}
              size="small"
              className={styles.cancelButton}
              onClick={handleModalAction(selectedUser.email, "COPY_ACTION")}
              label="COPY RESET LINK"
              leftIcon={
                <Tooltip
                  color="light"
                  position={"top"}
                  tooltip={`Click to copy a link that you can send to ${selectedUser.firstName} to reset their password.`}
                >
                  <NotificationIcon />
                </Tooltip>
              }
            />
          }
          rightButton={
            <Button
              isLoading={emailActionLoading}
              size="small"
              className={styles.actionButton}
              onClick={handleModalAction(selectedUser.email, "EMAIL_ACTION")}
              label="EMAIL RESET LINK"
              leftIcon={
                <Tooltip
                  color="light"
                  position={"top"}
                  tooltip={`${selectedUser.firstName} will receive an email with a link to reset their password.`}
                >
                  <NotificationIcon />
                </Tooltip>
              }
            />
          }
        />
      )}

      {showAccountModal && selectedAccount && (
        <AccountAddModal
          onCompleted={handleSearch}
          open={showAccountModal}
          onClose={handleAccountModalClose}
          customer={selectedAccount}
          me={me}
        />
      )}

      {showSchoolModal && selectedSchool && selectedAccount && (
        <SchoolAddModal
          key={`${selectedSchool.id}-${
            fullSchoolData ? "with-full-data" : "without-full-data"
          }`} // Force re-render when fullSchoolData changes
          account={{
            ...selectedAccount,
            // Make sure the account has the dataCollections property from the full data
            dataCollections:
              fullSchoolData?.accountSchools?.dataCollections ||
              selectedAccount.dataCollections,
          }}
          open={showSchoolModal}
          onClose={handleSchoolModalClose}
          school={
            fullSchoolData?.accountSchools?.schools?.find(
              (s) => s?.id === selectedSchool.id
            ) || selectedSchool
          }
          programOptions={sllProgramList}
        />
      )}

      <InviteSlidingPanel
        setBulkInviteSlide={setBulkInviteSlide}
        bulkInviteSlide={bulkInviteSlide}
        reLoadData={handleSearch}
      />
    </>
  );
}

export default SuperAdminUsers;
